import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { logger } from '@magnetar/utils'
import { Plus, Save, Trash2, GripVertical, Clock, X } from 'lucide-react'
import { AdScheduleTemplateSlot, AdScheduleTemplateSlotItem } from '@/hooks/useAdScheduleTemplates'
import { Advertisement } from '@magnetar/admin-api-interfaces'
import { AdScheduleTemplateSlotItemInput } from '@/services/ad_schedule_template_service'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AddAdvertisementDialog from './AddAdvertisementDialog'

interface SlotEditorProps {
  slot: AdScheduleTemplateSlot
  advertisements: Advertisement[]
  durationLimitEnabled?: boolean
  onSave: (
    slotName: string,
    playMinute: number,
    items: AdScheduleTemplateSlotItemInput[],
  ) => Promise<void>
  onDelete: () => Promise<void>
}

// 計算廣告的實際播放時長（秒）
// 從 advertisement.media 的 play_duration_ms 計算總和
// 如果 play_duration_ms < 1000，視為舊資料（秒數），需要 * 1000
const calculateActualDuration = (item: AdScheduleTemplateSlotItem): number => {
  if (!item.advertisement.media || item.advertisement.media.length === 0) {
    return 0
  }

  const totalMs = item.advertisement.media.reduce((sum, media) => {
    let durationMs = media.play_duration_ms || 0
    // 容錯處理：如果值小於 1000，可能是舊資料（秒數），轉換為毫秒
    if (durationMs > 0 && durationMs < 1000) {
      durationMs = durationMs * 1000
    }
    return sum + durationMs
  }, 0)

  return Math.round(totalMs / 1000) // 轉換為秒數並四捨五入
}

interface SlotItemProps {
  item: AdScheduleTemplateSlotItem
  onUpdate: (duration: number) => void
  onRemove: () => void
}

function SlotItem({ item, onRemove }: SlotItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.item_id.toString(),
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const actualDuration = calculateActualDuration(item)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
    >
      <div {...attributes} {...listeners} className="cursor-move">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{item.advertisement.title || '無標題廣告'}</p>
        <p className="text-sm text-muted-foreground">
          {item.advertisement.media.length} 個媒體檔案
        </p>
      </div>
      <div className="flex items-center gap-2">
        {/* <Input
          type="number"
          value={actualDuration}
          onChange={(e) => onUpdate(parseInt(e.target.value) || 0)}
          className="w-20 text-center"
          min={1}
          max={180}
          disabled
        /> */}
        <span className="text-sm text-muted-foreground">{actualDuration} 秒</span>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="text-destructive hover:text-destructive"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  )
}

export default function SlotEditor({ slot, advertisements, durationLimitEnabled = true, onSave, onDelete }: SlotEditorProps) {
  const [slotName, setSlotName] = useState(slot.slot_name || `梯次 ${slot.slot_number}`)
  const [playMinute, setPlayMinute] = useState(slot.play_minute || 0)
  const [items, setItems] = useState<AdScheduleTemplateSlotItem[]>(slot.slot_items)
  const [isSaving, setIsSaving] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const totalDuration = items.reduce((sum, item) => sum + calculateActualDuration(item), 0)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.item_id.toString() === active.id)
        const newIndex = items.findIndex((item) => item.item_id.toString() === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleAddAdvertisements = (selectedAds: Advertisement[]) => {
    const newItems: AdScheduleTemplateSlotItem[] = selectedAds.map((ad, index) => ({
      item_id: Date.now() + index, // 臨時 ID
      slot_id: slot.slot_id,
      advertisement_id: ad.advertisement_id,
      order: items.length + index,
      play_duration: 30, // 預設 30 秒
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_active: true,
      advertisement: {
        advertisement_id: ad.advertisement_id,
        supplier_id: ad.supplier_id,
        title: ad.title,
        description: ad.description,
        created_at: typeof ad.created_at === 'string' ? ad.created_at : ad.created_at.toISOString(),
        is_active: ad.is_active,
        media: ad.media || [], // 使用實際的 media 或預設空陣列
      },
    }))
    setItems([...items, ...newItems])
  }

  const handleUpdateItem = (index: number, duration: number) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], play_duration: duration }
    setItems(newItems)
  }

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const itemsInput: AdScheduleTemplateSlotItemInput[] = items.map((item, index) => ({
        advertisement_id: item.advertisement_id,
        order: index,
        // Backend sums play_duration to enforce the 180s slot limit, so send
        // the actual media length here (not a stored default).
        play_duration: Math.max(1, calculateActualDuration(item)),
        is_active: true,
      }))
      await onSave(slotName, playMinute, itemsInput)
    } catch (error) {
      logger.error('儲存失敗', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    await onDelete()
    setDeleteDialogOpen(false)
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Input
                  value={slotName}
                  onChange={(e) => setSlotName(e.target.value)}
                  className="h-8 w-48 font-semibold"
                />
                <Badge variant="secondary">梯次 {slot.slot_number}</Badge>
              </CardTitle>
              <CardDescription className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  總時長: {totalDuration} 秒{durationLimitEnabled ? ' / 180 秒' : ''}
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="play-minute" className="text-sm">
                    播放分鐘:
                  </Label>
                  <Input
                    id="play-minute"
                    type="number"
                    min="0"
                    max="59"
                    value={playMinute}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0
                      if (value >= 0 && value <= 59) {
                        setPlayMinute(value)
                      }
                    }}
                    className="h-8 w-16 text-center"
                  />
                </div>
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setAddDialogOpen(true)}>
                <Plus className="mr-1 h-4 w-4" />
                新增廣告
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {items.length > 0 ? (
            <ScrollArea className="h-[400px] pr-4">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={items.map((item) => item.item_id.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {items.map((item, index) => (
                      <SlotItem
                        key={item.item_id}
                        item={item}
                        onUpdate={(duration) => handleUpdateItem(index, duration)}
                        onRemove={() => handleRemoveItem(index)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </ScrollArea>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">尚未加入任何廣告</p>
              <Button variant="outline" className="mt-4" onClick={() => setAddDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                新增廣告
              </Button>
            </div>
          )}

          {durationLimitEnabled && totalDuration > 180 && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-lg">
              <p className="text-sm font-medium">總時長超過限制！請調整廣告時長或移除部分廣告。</p>
            </div>
          )}

          <Separator />

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={isSaving || (durationLimitEnabled && totalDuration > 180) || totalDuration === 0}
            >
              <Save className="mr-2 h-4 w-4" />
              儲存梯次
            </Button>
          </div>
        </CardContent>
      </Card>

      <AddAdvertisementDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        advertisements={advertisements}
        existingAdCounts={items.reduce<Record<number, number>>((acc, item) => {
          acc[item.advertisement_id] = (acc[item.advertisement_id] ?? 0) + 1
          return acc
        }, {})}
        onAdd={handleAddAdvertisements}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除梯次</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除「{slotName}」嗎？此操作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
